import LanguageSelector from "@/components/LanguageSelector";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PhoneInput } from "@/components/ui/PhoneInput";
import { Label } from "@/components/ui/label";
import { apiCalls } from "@/api/inspectorPortalQueries";
import { useAuthSession } from "@/contexts/AuthSessionContext";
import { normalizePhoneNumber } from "./authUtils";
import { useState } from "react";
import { useIntl } from "react-intl";

function getErrorStatus(error: unknown) {
  if (typeof error !== "object" || !error) return undefined;

  if ("status" in error && typeof error.status === "number") {
    return error.status;
  }

  if (
    "response" in error &&
    typeof error.response === "object" &&
    error.response &&
    "status" in error.response &&
    typeof error.response.status === "number"
  ) {
    return error.response.status;
  }

  return undefined;
}

export default function LoginPage() {
  const { startLogin } = useAuthSession();
  const intl = useIntl();
  const [value, setValue] = useState("");
  const [errorMessageId, setErrorMessageId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const errorId = "loginPhoneError";

  const submitLogin = async () => {
    const phoneNumber = normalizePhoneNumber(value);

    if (!phoneNumber.startsWith("+") || phoneNumber.length < 8) {
      setErrorMessageId("auth.login.invalidPhone");
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessageId(null);
      await apiCalls.requestInspectorPortalOtp(phoneNumber);
      startLogin({ phoneNumber });
    } catch (error) {
      const status = getErrorStatus(error);
      setErrorMessageId(
        status === 429 ? "auth.errors.throttled" : "auth.errors.generic",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-background p-6 md:p-10">
      <div className="mx-auto flex min-h-[80vh] w-full max-w-md items-center justify-center">
        <Card className="w-full">
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <CardTitle>
                {intl.formatMessage({ id: "auth.login.title" })}
              </CardTitle>
              <LanguageSelector />
            </div>
            <CardDescription>
              {intl.formatMessage({ id: "auth.login.description" })}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="loginPhone">
                {intl.formatMessage({ id: "auth.login.phoneLabel" })}
              </Label>
              <PhoneInput
                id="loginPhone"
                value={value}
                onChange={(phoneNumber) => {
                  setValue(phoneNumber);
                  setErrorMessageId(null);
                }}
                defaultCountry="DE"
                international
                placeholder={intl.formatMessage({
                  id: "auth.login.phonePlaceholder",
                })}
                aria-invalid={errorMessageId ? "true" : undefined}
                aria-describedby={errorMessageId ? errorId : undefined}
              />
            </div>
            {errorMessageId ? (
              <p id={errorId} role="alert" className="text-sm text-destructive">
                {intl.formatMessage({ id: errorMessageId })}
              </p>
            ) : null}
            <Button
              className="w-full"
              type="button"
              onClick={submitLogin}
              disabled={!value.trim() || isSubmitting}
            >
              {intl.formatMessage({
                id: isSubmitting
                  ? "auth.login.submitting"
                  : "auth.login.submit",
              })}
            </Button>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
